import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import EntertainmentCapacity from '../../components/EntertainmentCapacity';
import { getCapacityParticipantCount } from './capacityOverride';

function sumMatchingKeys(obj, re) {
  try {
    return Object.keys(obj || {}).reduce((sum, key) => {
      if (!re.test(key)) return sum;
      const value = Number(obj[key]);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  } catch {
    return 0;
  }
}

/**
 * Generic vertical view component for displaying bookings grouped by time slots.
 * Can be customized for different entertainment types with different card components.
 */
export default function VerticalViewBase({
  bookings = [],
  date,
  slotCapacity = 0,
  updateBooking,
  renderBookingCard,
  showCapacity = true,
  showPercentage = true,
  capacityData,
  capacityTimes,
  /**
   * Optional callback: (booking) => Array<{ label: string, sortKey: number, occupied: number }>
   * When provided, a booking can contribute its participant count to multiple time slots
   * (e.g. Golf interval bookings where groups start at different offsets).
   * When absent, all participants are counted at the booking's own start time.
   */
  resolveBookingSlotOccupancy,
  /**
   * Optional callback: (booking, slotDate) => occupiedCount
   * Allows a view to resolve occupancy for an already displayed slot label
   * based on whether a booking is active at that exact time.
   */
  resolveBookingOccupancyAtSlot,
  /**
   * When true (and resolveBookingSlotOccupancy is provided), a booking renders a
   * card in every slot it contributes to (each staggered start wave), instead of
   * only in its earliest slot. The per-slot wave metadata is passed to
   * renderBookingCard as a third argument.
   */
  renderCardPerSlot = false,
  /**
   * Optional callback: ({ booking, entry, isFirstEntry, renderCardPerSlot }) => boolean.
   * Allows views to render specific continuation entries while keeping other
   * continuation cards hidden.
   */
  shouldRenderCardForSlotEntry,
}) {
  const [capacityExpanded, setCapacityExpanded] = useState(false);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const slotListRef = useRef(null);
  const slotElementRefs = useRef(new Map());
  // Guards the initial auto-scroll: we only jump to the current time once, when
  // the user first enters the view. Subsequent recomputes (the per-minute clock
  // tick or booking/capacity refreshes) must not yank the user back, so they can
  // freely scroll to future bookings.
  const hasAutoScrolledRef = useRef(false);

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    []
  );

  const renderCapacityText = (occupied, capacity) => {
    if (!showCapacity) {
      return `${occupied} belegt`;
    }

    const cap = Number(capacity || 0);
    if (!Number.isFinite(cap) || cap <= 0) {
      return `${occupied} belegt`;
    }
    const free = cap - occupied;
    if (showPercentage) {
      const percentage = ((occupied / cap) * 100).toFixed(0);
      return `${occupied}/${cap} (${percentage}%)`;
    }
    return `${occupied}/${cap} (${free} frei)`;
  };

  const selectedYmd = useMemo(() => {
    if (!date) return null;
    try {
      if (typeof date?.get === 'function') {
        const y = Number(date.get('year'));
        const m = Number(date.get('month'));
        const d = Number(date.get('date'));
        if ([y, m, d].every(Number.isFinite)) return { y, m, d };
        return null;
      }
      if (date instanceof Date) {
        return { y: date.getFullYear(), m: date.getMonth(), d: date.getDate() };
      }
      return null;
    } catch {
      return null;
    }
  }, [date]);

  const isSelectedToday = useMemo(() => {
    if (!selectedYmd) return false;
    const now = new Date(nowTs);
    return (
      selectedYmd.y === now.getFullYear() &&
      selectedYmd.m === now.getMonth() &&
      selectedYmd.d === now.getDate()
    );
  }, [selectedYmd, nowTs]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const timeSlots = useMemo(() => {
    const groups = new Map();
    const rawCapacityTimes = Array.isArray(capacityTimes)
      ? capacityTimes
      : Array.isArray(capacityData?.times)
        ? capacityData.times
        : [];
    const capacityByTime = new Map(
      rawCapacityTimes.map(t => {
        const occupiedFromTeams = sumMatchingKeys(t, /^s\d+$/);
        const fallbackOccupied = Number(t?.capacityOccupied ?? t?.occupied ?? 0) || 0;
        const occupied = occupiedFromTeams > 0 ? occupiedFromTeams : fallbackOccupied;
        return [String(t?.time || ''), occupied];
      })
    );

    const addToGroup = (label, sortKey, booking, occupiedAdd) => {
      const prev = groups.get(label);
      if (!prev) {
        groups.set(label, {
          label,
          sortKey,
          bookings: [{ booking, wave: null }],
          occupied: occupiedAdd,
          capacity: Number(slotCapacity || 0),
        });
        return;
      }
      prev.sortKey = Math.min(prev.sortKey, sortKey);
      // Only add the booking card once (to the group of its earliest slot)
      if (!prev.bookings.some(entry => entry.booking === booking)) {
        prev.bookings.push({ booking, wave: null });
      }
      prev.occupied += occupiedAdd;
      prev.capacity = Number(slotCapacity || 0);
    };

    for (const booking of bookings || []) {
      const startDateStr =
        booking.bookingType === 'RegiondoBooking' ? booking.eventStart : booking.startTime;
      const startDate = new Date(startDateStr);
      const hasValidDate = Number.isFinite(startDate.getTime());

      if (selectedYmd && hasValidDate) {
        if (
          startDate.getFullYear() !== selectedYmd.y ||
          startDate.getMonth() !== selectedYmd.m ||
          startDate.getDate() !== selectedYmd.d
        ) {
          continue;
        }
      }

      if (resolveBookingSlotOccupancy) {
        // Caller provides per-slot occupancy breakdown (e.g. Golf groups)
        const entries = resolveBookingSlotOccupancy(booking, startDate, hasValidDate, timeFormatter);
        if (Array.isArray(entries) && entries.length > 0) {
          // Without renderCardPerSlot the booking card only appears once, in the
          // group of the first (earliest) slot. With renderCardPerSlot it appears
          // in every slot it contributes to, carrying its per-slot wave metadata.
          let firstEntry = true;
          for (const entry of entries) {
            const defaultPlaceCardHere = renderCardPerSlot || firstEntry;
            const placeCardHere =
              typeof shouldRenderCardForSlotEntry === 'function'
                ? Boolean(
                    shouldRenderCardForSlotEntry({
                      booking,
                      entry,
                      isFirstEntry: firstEntry,
                      renderCardPerSlot,
                    })
                  )
                : defaultPlaceCardHere;
            const cardEntry = placeCardHere ? { booking, wave: entry.wave || null } : null;
            const prev = groups.get(entry.label);
            if (!prev) {
              groups.set(entry.label, {
                label: entry.label,
                sortKey: entry.sortKey,
                bookings: cardEntry ? [cardEntry] : [],
                occupied: entry.occupied,
                capacity: Number(slotCapacity || 0),
              });
            } else {
              prev.sortKey = Math.min(prev.sortKey, entry.sortKey);
              if (cardEntry && !prev.bookings.some(existing => existing.booking === booking)) {
                prev.bookings.push(cardEntry);
              }
              prev.occupied += entry.occupied;
              prev.capacity = Number(slotCapacity || 0);
            }
            firstEntry = false;
          }
          continue;
        }
        // Fall through to default behaviour if callback returns nothing
      }

      const label = hasValidDate ? timeFormatter.format(startDate) : '??:??';
      const sortKey = hasValidDate ? startDate.getHours() * 60 + startDate.getMinutes() : 0;
      // Free places are derived from the occupancy, so a capacity override has to
      // be reflected here as well – otherwise a blocked slot still reads as free.
      const occupiedAdd = getCapacityParticipantCount(booking);
      addToGroup(label, sortKey, booking, occupiedAdd);
    }

    return Array.from(groups.values())
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(slot => {
        if (resolveBookingOccupancyAtSlot && selectedYmd) {
          const slotHour = Math.floor(slot.sortKey / 60);
          const slotMinute = slot.sortKey % 60;
          const slotDate = new Date(
            selectedYmd.y,
            selectedYmd.m,
            selectedYmd.d,
            slotHour,
            slotMinute,
            0,
            0
          );
          const occupiedFromBookings = (bookings || []).reduce((sum, booking) => {
            const occupied = Number(resolveBookingOccupancyAtSlot(booking, slotDate) || 0);
            return sum + (Number.isFinite(occupied) ? occupied : 0);
          }, 0);
          return {
            ...slot,
            occupied: occupiedFromBookings,
          };
        }

        const occupiedFromCapacity = capacityByTime.get(String(slot.label));
        if (!Number.isFinite(occupiedFromCapacity)) {
          return slot;
        }
        return {
          ...slot,
          occupied: occupiedFromCapacity,
        };
      });
  }, [
    bookings,
    slotCapacity,
    timeFormatter,
    selectedYmd,
    resolveBookingSlotOccupancy,
    resolveBookingOccupancyAtSlot,
    shouldRenderCardForSlotEntry,
    capacityTimes,
    capacityData?.times,
  ]);

  const getUtilizationMeta = (occupied, capacity) => {
    const cap = Number(capacity || 0);
    if (!Number.isFinite(cap) || cap <= 0) {
      return { ratio: null, color: 'primary' };
    }
    const ratio = cap <= 0 ? null : Math.max(0, Math.min(1, occupied / cap));
    if (ratio === null) {
      return { ratio: null, color: 'primary' };
    }
    if (ratio >= 0.9) return { ratio, color: 'error' };
    if (ratio >= 0.75) return { ratio, color: 'warning' };
    return { ratio, color: 'success' };
  };

  const capacityOverviewData = useMemo(() => {
    if (!capacityData) return null;

    const times = Array.isArray(capacityData?.times)
      ? capacityData.times
      : Array.isArray(capacityTimes)
        ? capacityTimes
        : null;

    if (!Array.isArray(times) || times.length === 0) {
      return Array.isArray(capacityData?.times) ? capacityData : null;
    }

    const sumMatchingKeys = (obj, re) => {
      try {
        return Object.keys(obj || {}).reduce((sum, key) => {
          if (!re.test(key)) return sum;
          const v = Number(obj[key]);
          return sum + (Number.isFinite(v) ? v : 0);
        }, 0);
      } catch {
        return 0;
      }
    };

    const cap = Number(capacityData?.capacity);

    const overviewTimes = times.map(t => {
      const occFromTeams = sumMatchingKeys(t, /^s\d+$/);
      const freeFromTeams = sumMatchingKeys(t, /^frees\d+$/);
      const fallbackOcc = Number(t?.capacityOccupied ?? t?.occupied ?? 0) || 0;
      const fallbackFree = Number(t?.capacityFree ?? t?.free ?? 0) || 0;
      const hasTeamKeys = occFromTeams > 0 || freeFromTeams !== 0;
      const occupied = hasTeamKeys ? occFromTeams : fallbackOcc;
      const free = hasTeamKeys ? freeFromTeams : fallbackFree;

      const pct =
        Number.isFinite(cap) && cap > 0
          ? Math.round((occupied / cap) * 100)
          : Number.isFinite(occupied + free) && occupied + free > 0
            ? Math.round((occupied / (occupied + free)) * 100)
            : 0;

      return {
        time: t?.time,
        occupied,
        free,
        percentageOccupied: pct,
      };
    });

    return {
      entertainmentName: capacityData?.entertainmentName,
      usesResources: capacityData?.usesResources ?? true,
      capacity: capacityData?.capacity,
      totalBookings: capacityData?.totalBookings,
      times: overviewTimes,
    };
  }, [capacityData, capacityTimes]);

  const currentSlotMeta = useMemo(() => {
    if (!isSelectedToday || !timeSlots.length) return null;

    const now = new Date(nowTs);
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    let closestPastOrCurrent = null;

    for (const slot of timeSlots) {
      const slotMinutes = Number(slot?.sortKey);
      if (!Number.isFinite(slotMinutes)) continue;
      if (slotMinutes <= nowMinutes) {
        closestPastOrCurrent = slot.label;
        continue;
      }
      break;
    }

    if (closestPastOrCurrent) {
      return {
        label: closestPastOrCurrent,
        isUpcoming: false,
      };
    }

    return timeSlots[0]?.label
      ? {
          label: timeSlots[0].label,
          isUpcoming: true,
        }
      : null;
  }, [isSelectedToday, nowTs, timeSlots]);

  useEffect(() => {
    if (hasAutoScrolledRef.current) return;
    if (!isSelectedToday || !currentSlotMeta?.label) return;

    const slotElement = slotElementRefs.current.get(currentSlotMeta.label);
    if (!slotElement) return;

    // The current slot is resolved and rendered: perform the one-time auto-scroll
    // and mark it done so later updates never re-scroll the user.
    hasAutoScrolledRef.current = true;

    const container = slotListRef.current;
    if (container) {
      const visibleTop = container.scrollTop + 12;
      const visibleBottom = container.scrollTop + container.clientHeight - 12;
      const slotTop = slotElement.offsetTop;
      const slotBottom = slotTop + slotElement.offsetHeight;

      if (slotTop >= visibleTop && slotBottom <= visibleBottom) return;
    }

    slotElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentSlotMeta, isSelectedToday]);

  return (
    <>
      {capacityOverviewData && (
        <div style={{ paddingBottom: '12px' }}>
          <EntertainmentCapacity
            data={capacityOverviewData}
            condensedMode={!capacityExpanded}
            onToggleExpand={() => setCapacityExpanded(v => !v)}
            currentDate={isSelectedToday ? new Date(nowTs) : null}
            autoScrollToCurrentTime={!capacityExpanded}
            highlightCurrentSlot
          />
        </div>
      )}

      <Paper
        ref={slotListRef}
        variant='outlined'
        sx={{
          width: '100%',
          position: 'relative',
          overflowY: 'auto',
          padding: 1.25,
          bgcolor: theme => alpha(theme.palette.background.paper, 0.6),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {timeSlots.map(slot => {
            const { ratio, color } = getUtilizationMeta(slot.occupied, slot.capacity);
            const isCurrentSlot = isSelectedToday && currentSlotMeta?.label === slot.label;

            return (
              <Paper
                key={slot.label}
                ref={element => {
                  if (!element) {
                    slotElementRefs.current.delete(slot.label);
                    return;
                  }
                  slotElementRefs.current.set(slot.label, element);
                }}
                variant='outlined'
                sx={{
                  p: 0.9,
                  scrollMarginTop: '12px',
                  borderLeft: theme =>
                    `4px solid ${alpha(theme.palette[color].main, showCapacity ? 1 : 0.5)}`,
                  ...(isCurrentSlot
                    ? {
                        borderColor: theme => theme.palette.info.main,
                        boxShadow: theme => `0 0 0 1px ${alpha(theme.palette.info.main, 0.45)}`,
                        backgroundColor: theme => alpha(theme.palette.info.main, 0.08),
                      }
                    : {}),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.25,
                  }}
                >
                  <Typography variant='subtitle1' sx={{ fontWeight: 700, flexShrink: 0 }}>
                    {slot.label}
                  </Typography>

                  {isCurrentSlot && (
                    <Chip
                      size='small'
                      color='info'
                      label={currentSlotMeta?.isUpcoming ? 'Als nächstes' : 'Jetzt'}
                      sx={{ fontWeight: 700, height: 22 }}
                    />
                  )}

                  {showCapacity && ratio !== null && (
                    <Box
                      sx={{
                        flex: '1 1 60px',
                        minWidth: 0,
                        height: 6,
                        borderRadius: 999,
                        bgcolor: theme => alpha(theme.palette.common.white, 0.08),
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.round(ratio * 1000) / 10}%`,
                          height: '100%',
                          bgcolor: theme => theme.palette[color].main,
                        }}
                      />
                    </Box>
                  )}

                  <Chip
                    size='small'
                    color={showCapacity ? color : 'default'}
                    variant={showCapacity ? 'filled' : 'outlined'}
                    label={renderCapacityText(slot.occupied, slot.capacity)}
                    sx={{ fontWeight: 700, flexShrink: 0, ml: 'auto' }}
                  />
                </Box>

                <Box sx={{ mt: 0.75 }}>
                  <Grid container spacing={1} alignItems='stretch'>
                    {slot.bookings.map(({ booking, wave }) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        xl={2}
                        key={`${booking.id || booking._id}@${slot.label}`}
                        sx={{ display: 'flex' }}
                      >
                        {renderBookingCard(booking, updateBooking, wave)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Paper>
    </>
  );
}
